import { CommonModule } from '@angular/common';
import { AfterViewChecked, Component, ElementRef, OnInit, ViewChild, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, RouterModule } from '@angular/router';
import DOMPurify from 'dompurify';
import {
    LeadLandingFormField,
    LeadLandingSubmissionDto,
    PublicLeadLandingPageDto,
    SubmitLeadLandingPageRequest
} from '../../models/lead-link.model';
import { LeadLinkService } from '../../services/lead-link.service';
import { isPublicLandingHost, publicLandingHostFromTenantSlug } from '../../guards/public-landing-domain.guard';
import { environment } from '../../../environments/environment';
import {
    buildLandingBaseCss,
    defaultAuroraMarketingUrl,
    ensureLandingTemplateClass,
    mergeLandingCssPreservingRules,
    mergeLandingThemeIntoCss,
    normalizeTemplateV2Key,
    normalizeLandingPublicHtml,
    projectFromLandingPage,
    resolveLandingPublicFooter,
    renderLandingProjectCss,
    renderLandingProjectHtml,
    renderLandingTemplateCssPrimer,
    resolveLandingPrimaryColor,
    sanitizeLandingCss
} from '../../utils/lead-landing-renderer';

@Component({
    selector: 'app-public-lead-landing-page',
    standalone: true,
    imports: [CommonModule, FormsModule, RouterModule],
    styleUrls: ['./public-lead-landing-page-footer.css'],
    template: `
        @if (loading()) {
            <main class="public-shell loading-state">
                <span class="spinner"></span>
            </main>
        } @else if (page() && isHtmlLanding()) {
            <main class="html-public-shell" [ngStyle]="landingStyles()">
                <div class="editor-style-host" [innerHTML]="editorStyleTag()"></div>
                <div class="html-content" [innerHTML]="htmlDocument()"></div>
                <form #htmlLeadForm class="lead-form lead-form--html lead-form--detached" (ngSubmit)="submit()">
                    @if (submitted()) {
                        <div class="success-state">
                            <i class="pi pi-check-circle"></i>
                            <h2>{{ page()?.success?.title || 'Richiesta inviata' }}</h2>
                            <p>
                                {{
                                    page()?.success?.message ||
                                        submission()?.message ||
                                        'Grazie, ti ricontatteremo a breve.'
                                }}
                            </p>
                        </div>
                    } @else {
                        <div class="form-header">
                            <h2>{{ page()?.form?.title || 'Richiedi informazioni' }}</h2>
                        </div>

                        @for (field of fields(); track field.key) {
                            <label>
                                <span
                                    >{{ field.label }}
                                    @if (field.required) {
                                        <span class="required-star">*</span>
                                    }
                                </span>
                                @if (field.type === 'textarea') {
                                    <textarea
                                        rows="4"
                                        [name]="field.key"
                                        [(ngModel)]="formData[field.key]"
                                        [required]="field.required === true"
                                    ></textarea>
                                } @else {
                                    <input
                                        [type]="inputType(field)"
                                        [name]="field.key"
                                        [(ngModel)]="formData[field.key]"
                                        [required]="field.required === true"
                                    />
                                }
                            </label>
                        }

                        @if (errorMessage()) {
                            <div class="form-error">{{ errorMessage() }}</div>
                        }

                        <button type="submit" [disabled]="submitting()">
                            @if (submitting()) {
                                Invio in corso
                            } @else {
                                {{ page()?.form?.submitLabel || page()?.content?.ctaLabel || 'Invia richiesta' }}
                            }
                        </button>
                    }
                </form>
                <ng-container *ngTemplateOutlet="publicLandingFooter" />
            </main>
        } @else if (page()) {
            <main class="public-shell" [ngStyle]="landingStyles()" [ngClass]="templateClass()">
                <section class="landing-hero">
                    <div class="hero-content">
                        @if (page()?.brand?.logoUrl) {
                            <img class="brand-logo" [src]="page()?.brand?.logoUrl" alt="" />
                        }
                        @if (page()?.content?.eyebrow) {
                            <span class="eyebrow">{{ page()?.content?.eyebrow }}</span>
                        }
                        <h1>{{ page()?.content?.headline }}</h1>
                        @if (page()?.content?.subtitle) {
                            <p class="subtitle">{{ page()?.content?.subtitle }}</p>
                        }
                        @if (page()?.content?.body) {
                            <p class="body-copy">{{ page()?.content?.body }}</p>
                        }
                        @if ((page()?.content?.bullets?.length ?? 0) > 0) {
                            <ul>
                                @for (bullet of page()?.content?.bullets ?? []; track bullet) {
                                    <li><span></span>{{ bullet }}</li>
                                }
                            </ul>
                        }
                    </div>

                    @if (page()?.content?.heroImageUrl) {
                        <figure class="hero-visual">
                            <img [src]="page()?.content?.heroImageUrl" alt="" />
                        </figure>
                    } @else {
                        <div class="hero-visual hero-visual--fallback">
                            <span>{{ page()?.content?.ctaLabel || 'Richiedi informazioni' }}</span>
                            <span class="visual-title">{{ page()?.content?.eyebrow || page()?.name }}</span>
                        </div>
                    }

                    <form class="lead-form" (ngSubmit)="submit()">
                        @if (submitted()) {
                            <div class="success-state">
                                <i class="pi pi-check-circle"></i>
                                <h2>{{ page()?.success?.title || 'Richiesta inviata' }}</h2>
                                <p>
                                    {{
                                        page()?.success?.message ||
                                            submission()?.message ||
                                            'Grazie, ti ricontatteremo a breve.'
                                    }}
                                </p>
                            </div>
                        } @else {
                            <div class="form-header">
                                <h2>{{ page()?.form?.title || 'Richiedi informazioni' }}</h2>
                            </div>

                            @for (field of fields(); track field.key) {
                                <label>
                                    <span
                                        >{{ field.label }}
                                        @if (field.required) {
                                            <span class="required-star">*</span>
                                        }
                                    </span>
                                    @if (field.type === 'textarea') {
                                        <textarea
                                            rows="4"
                                            [name]="field.key"
                                            [(ngModel)]="formData[field.key]"
                                            [required]="field.required === true"
                                        ></textarea>
                                    } @else {
                                        <input
                                            [type]="inputType(field)"
                                            [name]="field.key"
                                            [(ngModel)]="formData[field.key]"
                                            [required]="field.required === true"
                                        />
                                    }
                                </label>
                            }

                            @if (errorMessage()) {
                                <div class="form-error">{{ errorMessage() }}</div>
                            }

                            <button type="submit" [disabled]="submitting()">
                                @if (submitting()) {
                                    Invio in corso
                                } @else {
                                    {{ page()?.form?.submitLabel || page()?.content?.ctaLabel || 'Invia richiesta' }}
                                }
                            </button>
                        }
                    </form>
                </section>
                <ng-container *ngTemplateOutlet="publicLandingFooter" />
            </main>
        } @else {
            <main class="public-shell not-found">
                <h1>Pagina non disponibile</h1>
                <p>La landing page richiesta non è pubblicata o non esiste.</p>
                <a routerLink="/">Torna alla home</a>
            </main>
        }

        <ng-template #publicLandingFooter>
            <footer class="lp-page-footer">
                <div class="lp-page-footer-inner">
                    @if (footerLegalLine()) {
                        <p class="lp-page-footer-legal">{{ footerLegalLine() }}</p>
                    }
                    @if (footerPrivacyUrl() || footerTermsUrl()) {
                        <nav class="lp-page-footer-links" aria-label="Collegamenti legali">
                            @if (footerPrivacyUrl()) {
                                <a [href]="footerPrivacyUrl()" target="_blank" rel="noopener noreferrer">Privacy</a>
                            }
                            @if (footerTermsUrl()) {
                                <a [href]="footerTermsUrl()" target="_blank" rel="noopener noreferrer"
                                    >Termini di servizio</a
                                >
                            }
                        </nav>
                    }
                    <a
                        class="lp-page-footer-aurora"
                        [href]="auroraMarketingUrl"
                        target="_blank"
                        rel="noopener noreferrer"
                        >Powered by Aurora CRM</a
                    >
                </div>
            </footer>
        </ng-template>
    `,
    styles: [
        `
            :host {
                display: block;
                position: fixed;
                inset: 0;
                height: 100dvh;
                min-height: 100dvh;
                overflow-x: hidden;
                overflow-y: auto;
                background: var(--lp-bg, #f7f9fc);
                -webkit-overflow-scrolling: touch;
            }

            .public-shell {
                --primary: #2563eb;
                --accent: #10b981;
                min-height: 100%;
                background: #f8fafc;
                color: #0f172a;
                font-family:
                    Inter,
                    system-ui,
                    -apple-system,
                    BlinkMacSystemFont,
                    'Segoe UI',
                    sans-serif;
            }

            .html-public-shell {
                --primary: #2563eb;
                --accent: #10b981;
                display: flex;
                flex-direction: column;
                min-height: 100%;
                width: 100%;
                background: var(--lp-bg, #f7f9fc);
                color: #0f172a;
                font-family:
                    Inter,
                    system-ui,
                    -apple-system,
                    BlinkMacSystemFont,
                    'Segoe UI',
                    sans-serif;
            }

            .html-content {
                display: contents;
            }

            .editor-style-host {
                display: contents;
            }

            .landing-hero {
                min-height: 100dvh;
                display: grid;
                grid-template-columns: minmax(0, 1fr) minmax(300px, 0.62fr);
                grid-template-areas:
                    'copy form'
                    'visual form';
                gap: 1.65rem clamp(2rem, 5vw, 4rem);
                align-items: center;
                width: min(1140px, calc(100% - 40px));
                margin: 0 auto;
                padding: 3.5rem 0;
                background:
                    linear-gradient(120deg, color-mix(in srgb, var(--primary) 10%, transparent), transparent 48%),
                    #f8fafc;
            }

            .public-shell.template-offer .landing-hero {
                grid-template-columns: 0.72fr 1fr;
                grid-template-areas:
                    'copy copy'
                    'visual form';
                align-content: center;
                background: color-mix(in srgb, var(--primary) 8%, #f8fafc);
            }

            .public-shell.template-event .landing-hero {
                grid-template-columns: minmax(0, 0.92fr) minmax(320px, 0.72fr);
                grid-template-areas:
                    'visual form'
                    'copy form';
                background: #f7f5ff;
            }

            .hero-content {
                grid-area: copy;
                max-width: 720px;
            }

            .brand-logo {
                max-height: 56px;
                max-width: 220px;
                object-fit: contain;
                margin-bottom: 2rem;
            }

            .eyebrow {
                display: inline-flex;
                color: var(--primary);
                border: 1px solid color-mix(in srgb, var(--primary) 30%, #dbe3ef);
                border-radius: 999px;
                background: #fff;
                padding: 0.45rem 0.75rem;
                font-size: 0.88rem;
                line-height: 1;
                font-weight: 400;
                letter-spacing: 0;
                margin-bottom: 1rem;
            }

            h1 {
                margin: 0;
                max-width: 760px;
                font-size: clamp(2.4rem, 5vw, 3.6rem);
                line-height: 1;
                letter-spacing: 0;
                font-weight: 500;
                text-wrap: balance;
            }

            .subtitle {
                margin: 1.25rem 0 0;
                max-width: 680px;
                font-size: 1.18rem;
                color: #263446;
                line-height: 1.5;
                text-wrap: pretty;
            }

            .body-copy {
                margin: 1rem 0 0;
                max-width: 640px;
                color: #475569;
                line-height: 1.6;
            }

            ul {
                list-style: none;
                padding: 0;
                margin: 1.5rem 0 0;
                display: grid;
                gap: 0.65rem;
            }

            li {
                display: flex;
                align-items: center;
                gap: 0.65rem;
                color: #1e293b;
                font-weight: 600;
            }

            li span {
                width: 0.6rem;
                height: 0.6rem;
                border-radius: 999px;
                background: var(--accent);
                flex: 0 0 auto;
            }

            .hero-visual {
                grid-area: visual;
                min-height: 210px;
                border: 1px solid #dbe3ef;
                border-radius: 12px;
                overflow: hidden;
                background: #fff;
                margin: 0;
            }

            .hero-visual img {
                width: 100%;
                height: 100%;
                min-height: 210px;
                object-fit: cover;
                display: block;
            }

            .hero-visual--fallback {
                padding: 1.5rem;
                display: grid;
                align-content: end;
                gap: 0.5rem;
                background: linear-gradient(135deg, color-mix(in srgb, var(--primary) 18%, #ffffff), #ffffff 58%);
            }

            .hero-visual--fallback span {
                color: #475569;
                font-size: 0.9rem;
            }

            .hero-visual--fallback .visual-title {
                color: #0f172a;
                font-size: 1.5rem;
                line-height: 1.15;
                font-weight: 500;
            }

            .lead-form {
                grid-area: form;
                width: 100%;
                max-width: 500px;
                justify-self: end;
                background: #fff;
                border: 1px solid #e2e8f0;
                border-radius: 8px;
                box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
                padding: clamp(1.25rem, 3vw, 2rem);
                display: grid;
                gap: 1rem;
            }

            .lead-form--html {
                max-width: none;
                margin: 0;
                justify-self: stretch;
                background: transparent;
                border: 0;
                box-shadow: none;
                padding: 0;
            }

            .lead-form--detached {
                display: none;
            }

            .lead-form--mounted {
                display: grid;
            }

            .form-header h2,
            .success-state h2 {
                margin: 0;
                font-size: 1.35rem;
                letter-spacing: 0;
                font-weight: 500;
            }

            label {
                display: grid;
                gap: 0.35rem;
                color: #475569;
                font-size: 0.82rem;
                font-weight: 400;
            }

            .required-star {
                color: #dc2626;
            }

            input,
            textarea {
                width: 100%;
                border: 1px solid #cbd5e1;
                border-radius: 8px;
                padding: 0.75rem 0.85rem;
                font: inherit;
                color: #0f172a;
                background: #fff;
                outline: none;
            }

            input:focus,
            textarea:focus {
                border-color: var(--primary);
                box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary) 15%, transparent);
            }

            button[type='submit'] {
                border: 0;
                border-radius: 8px;
                min-height: 3rem;
                background: var(--primary);
                color: #fff;
                font-weight: 500;
                cursor: pointer;
            }

            button[type='submit']:disabled {
                opacity: 0.65;
                cursor: progress;
            }

            .form-error {
                border-radius: 8px;
                background: #fef2f2;
                color: #991b1b;
                padding: 0.75rem;
                font-size: 0.85rem;
            }

            .success-state {
                min-height: 260px;
                display: flex;
                flex-direction: column;
                justify-content: center;
                gap: 0.75rem;
                text-align: center;
            }

            .success-state i {
                color: var(--accent);
                font-size: 2.25rem;
            }

            .success-state p,
            .not-found p {
                color: #64748b;
                margin: 0;
                line-height: 1.5;
            }

            .loading-state,
            .not-found {
                display: flex;
                align-items: center;
                justify-content: center;
                flex-direction: column;
                gap: 1rem;
                text-align: center;
                padding: 2rem;
            }

            .spinner {
                width: 2.5rem;
                height: 2.5rem;
                border-radius: 999px;
                border: 3px solid #cbd5e1;
                border-top-color: #2563eb;
                animation: spin 0.9s linear infinite;
            }

            .not-found a {
                color: #2563eb;
                font-weight: 500;
            }

            @keyframes spin {
                to {
                    transform: rotate(360deg);
                }
            }

            @media (max-width: 900px) {
                .landing-hero,
                .public-shell.template-offer .landing-hero,
                .public-shell.template-event .landing-hero {
                    grid-template-columns: 1fr;
                    grid-template-areas:
                        'copy'
                        'visual'
                        'form';
                    width: min(100% - 28px, 680px);
                    min-height: auto;
                    padding: 2rem 0;
                }

                .lead-form {
                    justify-self: stretch;
                    max-width: none;
                }

                h1 {
                    font-size: clamp(2.1rem, 13vw, 3.8rem);
                }
            }
        `
    ]
})
export class PublicLeadLandingPageComponent implements OnInit, AfterViewChecked {
    @ViewChild('htmlLeadForm') private htmlLeadForm?: ElementRef<HTMLFormElement>;

    protected readonly page = signal<PublicLeadLandingPageDto | null>(null);
    protected readonly loading = signal(true);
    protected readonly submitting = signal(false);
    protected readonly submitted = signal(false);
    protected readonly submission = signal<LeadLandingSubmissionDto | null>(null);
    protected readonly errorMessage = signal<string | null>(null);
    private readonly publicHost = signal<string | null>(null);
    protected formData: Record<string, string> = {};

    protected readonly fields = computed<LeadLandingFormField[]>(() => this.page()?.form?.fields ?? []);
    private readonly resolvedFooter = computed(() => resolveLandingPublicFooter(this.page()));
    protected readonly footerLegalLine = computed(() => this.resolvedFooter().legalLine ?? '');
    protected readonly footerPrivacyUrl = computed(() => this.resolvedFooter().privacyPolicyUrl ?? '');
    protected readonly footerTermsUrl = computed(() => this.resolvedFooter().termsOfServiceUrl ?? '');
    protected readonly auroraMarketingUrl = defaultAuroraMarketingUrl(environment.production);
    protected readonly isHtmlLanding = computed(
        () =>
            this.page()?.content?.mode === 'html' &&
            Boolean(
                this.page()?.content?.editor?.html || projectFromLandingPage(this.page() as PublicLeadLandingPageDto)
            )
    );
    protected readonly sanitizedEditorCss = computed(() => {
        const page = this.page();
        if (!page) return '';
        const project = projectFromLandingPage(page);
        const editorCss = page.content?.editor?.css?.trim() || '';
        const templateKey = normalizeTemplateV2Key(page.templateKey);
        const baseCss = project
            ? buildLandingBaseCss(project)
            : [
                  renderLandingProjectCss(projectFromLandingPage(page)!),
                  renderLandingTemplateCssPrimer(templateKey)
              ].join('\n');
        const css = editorCss ? mergeLandingCssPreservingRules(baseCss, editorCss) : baseCss;
        const theme = {
            ...(project?.theme ?? {
                primaryColor: page.brand?.primaryColor,
                accentColor: page.brand?.accentColor,
                backgroundColor: '#f7f9fc',
                textColor: '#0f172a',
                fontFamily: page.brand?.fontFamily
            }),
            primaryColor: resolveLandingPrimaryColor(
                project?.theme?.primaryColor ?? page.brand?.primaryColor,
                project?.theme?.accentColor ?? page.brand?.accentColor
            )
        };
        return mergeLandingThemeIntoCss(sanitizeLandingCss(css), theme);
    });
    protected readonly editorStyleTag = computed<SafeHtml>(() =>
        this.domSanitizer.bypassSecurityTrustHtml(
            `<style>${this.tailwindUtilityCss()}${this.sanitizedEditorCss()}${this.formHostCss()}</style>`
        )
    );
    protected readonly htmlDocument = computed<SafeHtml>(() => {
        const page = this.page();
        if (!page) return this.domSanitizer.bypassSecurityTrustHtml('');
        const templateKey = normalizeTemplateV2Key(page.templateKey);
        const html = normalizeLandingPublicHtml(
            ensureLandingTemplateClass(page.content?.editor?.html || this.projectHtml(page), templateKey)
        );
        return this.domSanitizer.bypassSecurityTrustHtml(this.buildHtmlDocument(html));
    });

    private readonly route = inject(ActivatedRoute);
    private readonly leadLinkService = inject(LeadLinkService);
    private readonly domSanitizer = inject(DomSanitizer);

    ngOnInit(): void {
        const slug = this.route.snapshot.paramMap.get('slug') ?? '';
        const host = this.resolvePublicHost();

        if (!slug || !host) {
            this.loading.set(false);
            this.page.set(null);
            return;
        }

        this.publicHost.set(host);
        this.leadLinkService.getPublicLandingPage(host, slug).subscribe({
            next: (page) => {
                this.page.set(page);
                this.seedFormData(page.form?.fields ?? []);
                this.loading.set(false);
                document.title = page.seo?.['title']?.toString() || page.name;
            },
            error: () => {
                this.loading.set(false);
                this.page.set(null);
            }
        });
    }

    ngAfterViewChecked(): void {
        if (!this.isHtmlLanding()) return;
        this.mountHtmlForm();
    }

    submit(): void {
        const missing = this.fields().find((field) => field.required && !this.formData[field.key]?.trim());
        if (missing) {
            this.errorMessage.set(`Compila il campo ${missing.label}`);
            return;
        }

        const page = this.page();
        if (!page) return;
        const host = this.publicHost();
        if (!host) {
            this.errorMessage.set('Pagina non disponibile.');
            return;
        }

        this.errorMessage.set(null);
        this.submitting.set(true);
        this.leadLinkService.submitPublicLandingPage(host, page.slug, this.buildRequest()).subscribe({
            next: (submission) => {
                this.submission.set(submission);
                this.submitted.set(true);
                this.submitting.set(false);
            },
            error: (err: unknown) => {
                this.submitting.set(false);
                const candidate = err as { error?: { message?: string } };
                this.errorMessage.set(candidate.error?.message ?? 'Non siamo riusciti a inviare la richiesta.');
            }
        });
    }

    inputType(field: LeadLandingFormField): string {
        if (field.key === 'birthDate') return 'date';
        return field.type === 'textarea' ? 'text' : field.type;
    }

    landingStyles(): Record<string, string> {
        const page = this.page();
        const brand = page?.brand;
        const project = page ? projectFromLandingPage(page) : null;
        const primary = resolveLandingPrimaryColor(
            project?.theme?.primaryColor ?? brand?.primaryColor,
            project?.theme?.accentColor ?? brand?.accentColor
        );
        const accent = brand?.accentColor || '#16b981';
        return {
            '--primary': primary,
            '--accent': accent,
            '--lp-primary': primary,
            '--lp-accent': accent,
            '--lp-bg': '#f7f9fc',
            '--lp-ink': '#0f172a'
        };
    }

    templateClass(): string {
        const key = this.page()?.templateKey;
        if (key === 'offer' || key === 'offer-band') return 'template-offer';
        if (key === 'event' || key === 'event-focus') return 'template-event';
        return 'template-consulting';
    }

    private resolvePublicHost(): string | null {
        const tenantSlug = this.route.snapshot.paramMap.get('tenantSlug');
        if (tenantSlug) {
            return publicLandingHostFromTenantSlug(tenantSlug);
        }

        const hostname = typeof window === 'undefined' ? '' : window.location.hostname;
        return isPublicLandingHost(hostname) ? hostname : null;
    }

    private buildHtmlDocument(html: string): string {
        if (!html.trim()) {
            return '<div data-aurora-form-slot data-aurora-public-form-host></div>';
        }

        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        this.removeGeneratedFormArtifacts(doc);
        const slots = Array.from(doc.querySelectorAll('[data-aurora-form-slot]'));
        slots.slice(1).forEach((duplicateSlot) => duplicateSlot.remove());
        const slot = slots[0] ?? null;
        const formHost = doc.createElement('div');
        formHost.setAttribute('data-aurora-form-slot', '');
        formHost.setAttribute('data-aurora-public-form-host', '');

        if (slot) {
            slot.replaceWith(formHost);
        } else {
            doc.body.appendChild(formHost);
        }

        return DOMPurify.sanitize(doc.body.innerHTML, {
            FORBID_TAGS: [
                'script',
                'iframe',
                'object',
                'embed',
                'form',
                'input',
                'textarea',
                'button',
                'select',
                'option'
            ],
            FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'style'],
            ADD_ATTR: ['target', 'rel', 'data-aurora-form-slot', 'data-aurora-public-form-host']
        });
    }

    private removeGeneratedFormArtifacts(doc: Document): void {
        doc.querySelectorAll<HTMLElement>('.lead-form').forEach((element) => {
            if (element.matches('[data-aurora-form-slot]')) {
                element.classList.remove('lead-form');
                return;
            }

            if (element.querySelector('[data-aurora-form-slot]')) {
                element.classList.remove('lead-form');
                return;
            }

            element.remove();
        });
    }

    private mountHtmlForm(): void {
        const form = this.htmlLeadForm?.nativeElement;
        const host = document.querySelector<HTMLElement>('[data-aurora-public-form-host]');
        if (!form || !host) return;

        if (form.parentElement !== host) {
            host.replaceChildren(form);
        }

        form.classList.remove('lead-form--detached');
        form.classList.add('lead-form--mounted');
    }

    private projectHtml(page: PublicLeadLandingPageDto): string {
        const project = projectFromLandingPage(page);
        return project ? renderLandingProjectHtml(project) : '';
    }

    private projectCss(page: PublicLeadLandingPageDto): string {
        const project = projectFromLandingPage(page);
        return project ? renderLandingProjectCss(project) : '';
    }

    private tailwindUtilityCss(): string {
        return `
.tw-relative{position:relative}.tw-absolute{position:absolute}.tw-inset-0{inset:0}.tw-z-10{z-index:10}.tw-hidden{display:none}.tw-block{display:block}.tw-inline-flex{display:inline-flex}.tw-flex{display:flex}.tw-grid{display:grid}.tw-contents{display:contents}
.tw-w-full{width:100%}.tw-h-full{height:100%}.tw-min-h-screen{min-height:100vh}.tw-max-w-screen-xl{max-width:1280px}.tw-max-w-7xl{max-width:80rem}.tw-max-w-6xl{max-width:72rem}.tw-max-w-5xl{max-width:64rem}.tw-max-w-3xl{max-width:48rem}.tw-mx-auto{margin-left:auto;margin-right:auto}
.tw-items-start{align-items:flex-start}.tw-items-center{align-items:center}.tw-items-end{align-items:flex-end}.tw-justify-start{justify-content:flex-start}.tw-justify-center{justify-content:center}.tw-justify-between{justify-content:space-between}.tw-justify-end{justify-content:flex-end}
.tw-grid-cols-1{grid-template-columns:repeat(1,minmax(0,1fr))}.tw-grid-cols-2{grid-template-columns:repeat(2,minmax(0,1fr))}.tw-grid-cols-3{grid-template-columns:repeat(3,minmax(0,1fr))}.tw-flex-col{flex-direction:column}.tw-flex-wrap{flex-wrap:wrap}
.tw-gap-2{gap:.5rem}.tw-gap-3{gap:.75rem}.tw-gap-4{gap:1rem}.tw-gap-5{gap:1.25rem}.tw-gap-6{gap:1.5rem}.tw-gap-8{gap:2rem}.tw-gap-10{gap:2.5rem}.tw-gap-12{gap:3rem}.tw-gap-16{gap:4rem}
.tw-p-0{padding:0}.tw-p-4{padding:1rem}.tw-p-5{padding:1.25rem}.tw-p-6{padding:1.5rem}.tw-p-8{padding:2rem}.tw-p-10{padding:2.5rem}.tw-p-12{padding:3rem}.tw-px-4{padding-left:1rem;padding-right:1rem}.tw-px-6{padding-left:1.5rem;padding-right:1.5rem}.tw-px-8{padding-left:2rem;padding-right:2rem}.tw-py-4{padding-top:1rem;padding-bottom:1rem}.tw-py-8{padding-top:2rem;padding-bottom:2rem}.tw-py-12{padding-top:3rem;padding-bottom:3rem}.tw-py-16{padding-top:4rem;padding-bottom:4rem}.tw-py-20{padding-top:5rem;padding-bottom:5rem}
.tw-mt-2{margin-top:.5rem}.tw-mt-4{margin-top:1rem}.tw-mt-6{margin-top:1.5rem}.tw-mt-8{margin-top:2rem}.tw-mt-10{margin-top:2.5rem}.tw-mt-12{margin-top:3rem}.tw-mb-2{margin-bottom:.5rem}.tw-mb-4{margin-bottom:1rem}.tw-mb-8{margin-bottom:2rem}
.tw-rounded-md{border-radius:.375rem}.tw-rounded-lg{border-radius:.5rem}.tw-rounded-xl{border-radius:.75rem}.tw-rounded-2xl{border-radius:1rem}.tw-rounded-3xl{border-radius:1.5rem}.tw-rounded-full{border-radius:9999px}.tw-overflow-hidden{overflow:hidden}
.tw-border{border:1px solid #e2e8f0}.tw-border-slate-200{border-color:#e2e8f0}.tw-border-slate-300{border-color:#cbd5e1}.tw-bg-white{background:#fff}.tw-bg-slate-50{background:#f8fafc}.tw-bg-slate-100{background:#f1f5f9}.tw-bg-slate-900{background:#0f172a}.tw-bg-black{background:#000}.tw-bg-primary{background:var(--primary,#3157e6)}.tw-bg-accent{background:var(--accent,#16b981)}
.tw-text-white{color:#fff}.tw-text-slate-400{color:#94a3b8}.tw-text-slate-500{color:#64748b}.tw-text-slate-600{color:#475569}.tw-text-slate-700{color:#334155}.tw-text-slate-800{color:#1e293b}.tw-text-slate-900{color:#0f172a}.tw-text-primary{color:var(--primary,#3157e6)}.tw-text-accent{color:var(--accent,#16b981)}
.tw-text-xs{font-size:.75rem;line-height:1rem}.tw-text-sm{font-size:.875rem;line-height:1.25rem}.tw-text-base{font-size:1rem;line-height:1.5rem}.tw-text-lg{font-size:1.125rem;line-height:1.75rem}.tw-text-xl{font-size:1.25rem;line-height:1.75rem}.tw-text-2xl{font-size:1.5rem;line-height:2rem}.tw-text-3xl{font-size:1.875rem;line-height:2.25rem}.tw-text-4xl{font-size:2.25rem;line-height:2.5rem}.tw-text-5xl{font-size:3rem;line-height:1.05}
.tw-font-normal{font-weight:400}.tw-font-medium{font-weight:500}.tw-font-semibold{font-weight:600}.tw-font-bold{font-weight:700}.tw-leading-tight{line-height:1.15}.tw-leading-snug{line-height:1.3}.tw-leading-relaxed{line-height:1.65}.tw-tracking-wide{letter-spacing:.025em}.tw-uppercase{text-transform:uppercase}
.tw-shadow-sm{box-shadow:0 1px 2px rgba(15,23,42,.08)}.tw-shadow-lg{box-shadow:0 18px 45px rgba(15,23,42,.12)}.tw-shadow-xl{box-shadow:0 24px 70px rgba(15,23,42,.16)}
@media (min-width:768px){.md\\:tw-grid{display:grid}.md\\:tw-flex{display:flex}.md\\:tw-grid-cols-2{grid-template-columns:repeat(2,minmax(0,1fr))}.md\\:tw-grid-cols-3{grid-template-columns:repeat(3,minmax(0,1fr))}.md\\:tw-flex-row{flex-direction:row}.md\\:tw-p-10{padding:2.5rem}.md\\:tw-p-12{padding:3rem}.md\\:tw-py-20{padding-top:5rem;padding-bottom:5rem}.md\\:tw-text-5xl{font-size:3rem;line-height:1.05}}
@media (min-width:1024px){.lg\\:tw-grid-cols-2{grid-template-columns:repeat(2,minmax(0,1fr))}.lg\\:tw-grid-cols-3{grid-template-columns:repeat(3,minmax(0,1fr))}.lg\\:tw-text-6xl{font-size:3.75rem;line-height:1}}
`;
    }

    private formHostCss(): string {
        return `
.lead-form, .lead-form * { box-sizing: border-box; }
.lead-form {
  width: 100%;
  min-width: 0;
  color: inherit;
  font: inherit;
  display: grid;
  gap: var(--lp-form-gap, 14px);
  background: var(--lp-form-background, #fff);
  border: var(--lp-form-border, 1px solid #dbe3ef);
  border-radius: var(--lp-form-radius, var(--lp-radius, 12px));
  box-shadow: var(--lp-form-shadow, var(--lp-shadow, 0 4px 24px rgba(0, 0, 0, 0.1)));
  padding: var(--lp-form-padding, 24px);
}
.lead-form h2 {
  margin: 0;
  color: inherit;
  font-size: clamp(20px, 2vw, 28px);
  line-height: 1.18;
  letter-spacing: 0;
  font-weight: 620;
}
.lead-form .form-header { display: grid; gap: 6px; }
.lead-form label {
  display: grid;
  grid-template-columns: 1fr;
  gap: 7px;
  margin: 0;
  color: color-mix(in srgb, currentColor 74%, #64748b);
  font-size: 14px;
  line-height: 1.25;
  font-weight: 450;
}
.lead-form label > span {
  display: inline-flex;
  align-items: baseline;
  gap: 3px;
}
.lead-form input,
.lead-form textarea {
  display: block;
  width: 100%;
  max-width: 100%;
  min-height: 44px;
  border: 1px solid color-mix(in srgb, currentColor 18%, transparent);
  border-radius: var(--lp-input-radius, 8px);
  padding: 10px 12px;
  background: color-mix(in srgb, #ffffff 94%, transparent);
  color: #0f172a;
  font: inherit;
  line-height: 1.35;
  resize: vertical;
  position: static;
  transform: none;
}
.lead-form textarea { min-height: 110px; }
.lead-form button[type='submit'] {
  width: 100%;
  min-height: 48px;
  border: 0;
  border-radius: var(--lp-button-radius, 8px);
  background: var(--primary);
  color: #fff;
  font: inherit;
  font-weight: 650;
  padding: 0 18px;
  cursor: pointer;
}
[data-aurora-form-slot] {
  width: 100%;
  max-width: 100%;
  min-width: 0;
}
@media (max-width: 860px) {
  .lp-form-section .lp-section-inner {
    grid-template-columns: 1fr;
  }
  .lp-form-section [data-aurora-form-slot] {
    max-width: 100%;
    margin: 0;
  }
}
@media (max-width: 760px) {
  .lp-page {
    overflow-x: hidden;
  }
  .lp-page [data-aurora-form-slot],
  .lp-page .lead-form {
    grid-column: 1 / -1;
    width: 100%;
    max-width: 100%;
    min-width: 0;
  }
  .lead-form {
    padding: var(--lp-form-mobile-padding, var(--lp-form-padding, 0));
  }
  .lead-form h2 {
    font-size: clamp(20px, 7vw, 26px);
  }
}
`;
    }

    private seedFormData(fields: LeadLandingFormField[]): void {
        this.formData = {};
        for (const field of fields) {
            this.formData[field.key] = '';
        }
    }

    private buildRequest(): SubmitLeadLandingPageRequest {
        const customFields: Record<string, unknown> = {};
        const standardKeys = new Set([
            'firstName',
            'lastName',
            'email',
            'phone',
            'taxCode',
            'birthDate',
            'address',
            'city',
            'province',
            'region',
            'postalCode',
            'category',
            'channel',
            'message'
        ]);

        for (const field of this.fields()) {
            if (!standardKeys.has(field.key) && this.formData[field.key]) {
                customFields[field.key] = this.formData[field.key];
            }
        }

        return {
            firstName: this.formData['firstName'] ?? '',
            lastName: this.formData['lastName'] ?? '',
            email: this.formData['email'] || null,
            phone: this.formData['phone'] ?? '',
            taxCode: this.formData['taxCode'] || null,
            birthDate: this.formData['birthDate'] || null,
            address: this.formData['address'] || null,
            city: this.formData['city'] || null,
            province: this.formData['province'] || null,
            region: this.formData['region'] || null,
            postalCode: this.formData['postalCode'] || null,
            category: this.formData['category'] || null,
            channel: this.formData['channel'] || null,
            message: this.formData['message'] || null,
            customFields: Object.keys(customFields).length ? customFields : null,
            utm: this.extractUtm(),
            referrer: document.referrer || null
        };
    }

    private extractUtm(): Record<string, string | null> {
        const utm: Record<string, string | null> = {};
        for (const [key, value] of Object.entries(this.route.snapshot.queryParams)) {
            if (key.startsWith('utm_')) {
                utm[key] = value?.toString() ?? null;
            }
        }
        return utm;
    }
}
